import { Tooltip } from 'antd';
import classNames from 'classnames';
import parse from 'html-react-parser';
import React, {
  CSSProperties,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { combineUserName } from '@helpers/combineUserName';
import UserAvatar from '@components/UserAvatar';
import {
  AssetVersionCommentGroupMentionDto,
  AssetVersionCommentMentionDto,
  AssetVersionCommentTimecodeDto
} from '@api/Api';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { useResponsive } from '@hooks/useResponsive';
import { formatDuration } from '@helpers/formatDuration';
import commentShortTime from '@helpers/commentShortTime';

function MarkTooltipContent({
  user,
  comment,
  createdAt,
  timeCode,
  mentions,
  groupMentions,
  onReadMore
}: {
  user: any;
  comment: string;
  createdAt: string;
  timeCode?: AssetVersionCommentTimecodeDto | null;
  mentions: AssetVersionCommentMentionDto[];
  groupMentions: AssetVersionCommentGroupMentionDto[];
  onReadMore: () => void;
}) {
  const { isMdWidth } = useResponsive();
  const [isShowButton, setIsShowButton] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  const prepareHtml = useCallback(() => {
    let html = comment;
    mentions.forEach((el) => {
      html = html.replaceAll(
        `{{${el.id}}}`,
        `<span class="b-suggestion-user">@${combineUserName(el)}</span>`
      );
    });
    groupMentions.forEach((el) => {
      html = html.replaceAll(
        `{{${el.id}}}`,
        `<span class="b-suggestion-user">@${el.name}</span>`
      );
    });
    html = html.replaceAll(
      /{{(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})}}/g,
      `<span class="b-suggestion-user">@Deleted User</span>`
    );
    return parse(html);
  }, [comment, mentions, groupMentions]);

  const checkTruncation = useCallback(() => {
    const el = textRef.current;
    if (!el) {
      setIsShowButton(false);
      return;
    }
    setIsShowButton(el.scrollHeight > el.clientHeight + 1);
  }, []);

  useLayoutEffect(() => {
    checkTruncation();
  }, [checkTruncation, comment, timeCode]);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTruncation]);

  const timecodeLabel =
    timeCode?.fromSeconds !== undefined
      ? formatDuration(timeCode.fromSeconds)
      : null;

  return (
    <div className="mark_tooltip">
      <div className="mark_tooltip__header">
        <UserAvatar
          isActive={true}
          size={24}
          src={user?.picture.url || ''}
          userEmail={user?.email || ''}
          userName={user?.name || ''}
        />
        <span className="mark_tooltip__name">
          {user ? combineUserName(user) : 'Deleted User'}
        </span>
        <span className="mark_tooltip__posted">
          {commentShortTime(createdAt)}
        </span>
      </div>
      <div ref={textRef} className="mark_tooltip__content">
        {timecodeLabel && (
          <span className="mark_tooltip__timecode">{timecodeLabel}</span>
        )}
        {prepareHtml()}
      </div>
      {isShowButton && !isMdWidth && (
        <button
          type="button"
          className="mark_tooltip__read-more"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onReadMore();
          }}
        >
          Read more
        </button>
      )}
    </div>
  );
}

function Mark({
  id,
  user,
  color,
  comment,
  createdAt,
  timeCode,
  isExpand,
  isRange,
  currentTime,
  mentions,
  groupMentions,
  setSelectedCollapse
}: {
  id: string;
  color: string;
  user: any;
  comment: string;
  createdAt: string;
  timeCode?: AssetVersionCommentTimecodeDto | null;
  isExpand: boolean;
  isRange: boolean;
  currentTime: number;
  mentions: AssetVersionCommentMentionDto[];
  groupMentions: AssetVersionCommentGroupMentionDto[];
  setSelectedCollapse?: (collapse: string | null) => void;
}) {
  const { isMdWidth } = useResponsive();
  const { selectedCommentId, canvasMode, selectComment, setCanvasMode } =
    useAssetViewerState();

  if (isExpand || canvasMode === 'edit') return null;

  const timeFromSeconds = timeCode?.fromSeconds;
  const shouldShowMobileTooltip =
    isMdWidth &&
    !selectedCommentId &&
    timeFromSeconds != null &&
    Math.abs(timeFromSeconds - currentTime) <= 0.25;

  const onSelectComment = () => {
    setCanvasMode('view');
    selectComment(id);
  };

  const onReadMore = () => {
    setSelectedCollapse?.('comments');
    onSelectComment();
  };

  const mobileTooltip =
    shouldShowMobileTooltip &&
    createPortal(
      <div
        className="mark_tooltip_side"
        style={
          {
            '--mark-border-color': color
          } as CSSProperties
        }
      >
        <MarkTooltipContent
          user={user}
          comment={comment}
          createdAt={createdAt}
          timeCode={timeCode}
          mentions={mentions}
          groupMentions={groupMentions}
          onReadMore={onReadMore}
        />
      </div>,
      document.body
    );

  return (
    <>
      {mobileTooltip}
      <span
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSelectComment();
        }}
        className={classNames(
          selectedCommentId === id ? (isRange ? 'for_range' : 'for_pin') : '',
          'mark'
        )}
      >
        <Tooltip
          placement="top"
          overlayClassName="selected_mark_overlay"
          overlayStyle={
            {
              '--mark-border-color': color
            } as CSSProperties
          }
          overlayInnerStyle={{ maxWidth: 300 }}
          getPopupContainer={() =>
            document.querySelector('.video-js') as HTMLElement
          }
          title={
            <MarkTooltipContent
              user={user}
              comment={comment}
              createdAt={createdAt}
              timeCode={timeCode}
              mentions={mentions}
              groupMentions={groupMentions}
              onReadMore={onReadMore}
            />
          }
          trigger={isMdWidth ? [] : ['hover']}
        >
          <svg
            width="14"
            height="13"
            style={
              selectedCommentId && selectedCommentId !== id
                ? { opacity: 0.3 }
                : selectedCommentId === id
                ? { transform: 'scale(1.5)' }
                : {}
            }
            viewBox="0 0 14 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.61803 11.999L10.382 11.999C11.8687 11.999 12.8357 10.4344 12.1708 9.10459L8.78885 2.34066C8.05181 0.866567 5.94819 0.866568 5.21115 2.34066L6.10557 2.78788L5.21115 2.34066L1.82918 9.1046C1.16428 10.4344 2.13127 11.999 3.61803 11.999Z"
              fill={color}
              stroke="#292B2E"
              strokeWidth="2"
            />
          </svg>
        </Tooltip>
      </span>
    </>
  );
}

export default Mark;
